import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => (
  <section id="home" data-testid="hero-section" className="relative z-10 min-h-screen flex items-center">
    <div className="relative z-10 mx-auto w-full max-w-7xl px-6 sm:px-10 pt-28 pb-20">
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.3, duration: 0.8 }}
        className="font-display text-[11px] sm:text-xs font-medium tracking-[0.5em] text-white/50 uppercase"
      >
        Premium Web Design Studio
      </motion.p>

      <h1
        data-testid="hero-headline"
        className="font-display mt-8 text-[clamp(2.6rem,7.5vw,6.5rem)] font-semibold leading-[1.0] tracking-tighter text-white max-w-5xl"
      >
        <MaskedLine delay={2.35}>Websites Built to</MaskedLine>
        <MaskedLine delay={2.45} className="chrome-text">Launch Businesses</MaskedLine>
        <MaskedLine delay={2.55}>
          Forward<span className="font-serif-accent font-medium text-white/90">.</span>
        </MaskedLine>
      </h1>

      <motion.p
        data-testid="hero-subtext"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.75, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-white/55"
      >
        CLOSER. designs premium digital experiences that help businesses look exceptional,
        attract customers and grow online.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <button
          data-testid="hero-start-project-button"
          onClick={() => scrollToSection("contact")}
          className="group inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04] active:scale-95"
        >
          Start Your Project
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
        <button
          data-testid="hero-explore-work-button"
          onClick={() => scrollToSection("work")}
          className="inline-flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/10"
        >
          Explore Our Work
        </button>
      </motion.div>
    </div>

    <motion.button
      data-testid="hero-scroll-indicator"
      onClick={() => scrollToSection("services")}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 3.4, duration: 1 }}
      className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40 hover:text-white/80 transition-colors duration-300"
      aria-label="Scroll down"
    >
      <span className="text-[10px] tracking-[0.4em] uppercase font-display">Scroll</span>
      <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
        <ChevronDown size={16} />
      </motion.span>
    </motion.button>
  </section>
);

export default Hero;
